import { ChevronLeft, Loader2 } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import logoColor from '@/assets/brand/signcare-logo.svg';

import { useQuestionnaire } from '../api/useQuestionnaire';
import {
  type QuestionnaireAnswers,
  type RenderableSection,
  type SectionErrors,
  toRenderableField,
  totalUploadBytes,
  validateSection,
} from '../elements/answers';
import { QuestionnaireRenderer } from '../elements/QuestionnaireRenderer';

/**
 * The questionnaire as an applicant will meet it, full-screen.
 *
 * WHAT IT IS FOR. The builder's phone preview is 388px inside a 528px column at 85% scale, which is
 * fine for checking that a field is there and useless for reading a long consent statement or
 * judging whether a five-tier cascade is comfortable to use. This is the same form at real size, so
 * "Preview" in the list means what it says.
 *
 * IT IS THE SAME COMPONENT AS THE LIVE RUN — `QuestionnaireRenderer`, with the same
 * `validateSection`. Answers are local and discarded; nothing is submitted and no session exists. So
 * an admin walking this hits exactly the validation an applicant would, which a static mock-up
 * cannot tell them.
 *
 * Deliberately NOT the applicant route: it needs a logged-in admin, reads the questionnaire directly
 * rather than through a session's frozen snapshot, and stores nothing.
 */
export function QuestionnairePreviewScreen() {
  const { questionnaireId } = useParams<{ questionnaireId: string }>();
  const { data, isLoading, error } = useQuestionnaire(questionnaireId);

  const [index, setIndex] = useState(0);
  const [answers, setAnswers] = useState<QuestionnaireAnswers>({});
  const [errors, setErrors] = useState<SectionErrors>({});

  // Empty sections dropped, exactly as the runner drops them — an admin can leave one empty while
  // building, and a preview that shows a blank screen the applicant will never see is misleading.
  const sections = useMemo<RenderableSection[]>(() => {
    if (!data) return [];
    return data.sections
      .filter((s) => s.fields.length > 0)
      .map((s) => ({
        id: s.id,
        title: s.title?.trim() || s.name,
        description: s.description ?? '',
        fields: s.fields.map(toRenderableField),
      }));
  }, [data]);

  if (isLoading) {
    return (
      <PreviewFrame>
        <div className="flex flex-1 items-center justify-center gap-2 text-[length:var(--text-14)] text-[#666666]">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading preview…
        </div>
      </PreviewFrame>
    );
  }

  if (error || sections.length === 0) {
    return (
      <PreviewFrame>
        <div className="flex flex-1 flex-col items-center justify-center gap-2 text-center">
          <h2 className="text-base font-semibold text-[#041A3A]">Nothing to preview yet</h2>
          <p className="max-w-xs text-[length:var(--text-14)] text-[#666666]">
            Add at least one question to a section, then come back.
          </p>
          <Link
            to={`/questionnaires/${questionnaireId}/builder`}
            className="mt-2 rounded-lg bg-primary px-4 py-2 text-[length:var(--text-14)] font-medium text-white"
          >
            Open the builder
          </Link>
        </div>
      </PreviewFrame>
    );
  }

  const current = Math.min(index, sections.length - 1);
  const section = sections[current];
  if (!section) return null;

  const sectionAnswers = answers[section.id] ?? {};
  const isLast = current === sections.length - 1;
  const progress = Math.round(((current + 1) / sections.length) * 100);

  function handleChange(fieldId: string, value: unknown) {
    if (!section) return;
    setAnswers((prev) => ({
      ...prev,
      [section.id]: { ...(prev[section.id] ?? {}), [fieldId]: value },
    }));
    setErrors((prev) => {
      if (!(fieldId in prev)) return prev;
      const next = { ...prev };
      delete next[fieldId];
      return next;
    });
  }

  function handleContinue() {
    if (!section) return;
    const found = validateSection(section, sectionAnswers);
    if (Object.keys(found).length > 0) {
      setErrors(found);
      return;
    }
    setErrors({});
    // Wraps on the last section rather than submitting — there is nothing to submit, and a dead
    // button at the end would make the preview feel broken.
    setIndex(isLast ? 0 : current + 1);
  }

  return (
    <PreviewFrame questionnaireId={questionnaireId}>
      <div className="mb-4 h-1 w-full shrink-0 overflow-hidden rounded-full bg-[#E8EEF9]">
        <div
          className="h-full rounded-full bg-primary transition-[width] duration-300"
          style={{ width: `${progress}%` }}
        />
      </div>

      <div className="custom-scrollbar flex min-h-0 flex-1 flex-col items-center overflow-y-auto pb-2 text-center">
        <QuestionnaireRenderer
          section={section}
          answers={sectionAnswers}
          // Across every section, so the preview shows the same remaining upload budget an
          // applicant would see — the point of sharing one renderer.
          stepUploadBytes={totalUploadBytes(answers)}
          onChange={handleChange}
          errors={errors}
          logoUrl={logoColor}
        />
      </div>

      <div className="mt-auto flex shrink-0 flex-col gap-2.5 pt-3 pb-2">
        {/* No "Section 1 of 2" counter — removed from the applicant's runner (see
            QuestionnaireStepRunner), and this screen exists to show exactly what they will see.
            "Back to builder" above stays: that is the admin's way out of the preview, not part of
            the flow being previewed. */}
        <button
          type="button"
          onClick={handleContinue}
          className="w-full rounded-xl bg-primary py-2.5 text-[14px] font-semibold text-white shadow-xs transition-colors hover:bg-[#0038CC]"
        >
          {isLast ? 'Start again' : 'Continue'}
        </button>
        <div className="flex items-center justify-center gap-1.5 text-[length:var(--text-10)] text-primary">
          <span>Secured by</span>
          <img src={logoColor} alt="SignCare" className="h-3 w-auto max-w-[84px] object-contain" />
        </div>
      </div>
    </PreviewFrame>
  );
}

/**
 * A phone-width column, centred, with the app chrome deliberately absent — the same treatment the
 * session preview page gets. Sidebar and topbar would change the width the form is judged at, which
 * is the one thing this page exists to show.
 */
function PreviewFrame({
  children,
  questionnaireId,
}: {
  children: React.ReactNode;
  questionnaireId?: string;
}) {
  return (
    <div className="flex min-h-dvh w-full flex-col items-center bg-white md:bg-[#F4F6FD]">
      <div className="relative mx-auto flex h-dvh w-full max-w-[480px] flex-col bg-white px-6 pt-4 pb-4 md:shadow-[0_0_0_1px_#E5E7EB]">
        {questionnaireId && (
          <Link
            to={`/questionnaires/${questionnaireId}/builder`}
            className="mb-2 flex shrink-0 items-center gap-1 self-start text-[length:var(--text-12)] font-medium text-[#64748B] transition-colors hover:text-primary"
          >
            <ChevronLeft size={14} />
            Back to builder
          </Link>
        )}
        {children}
      </div>
    </div>
  );
}
